import React, { useContext, useEffect, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import { VerticalFieldAndValueWithIcon } from '@components/ringil3/Containers/VerticalFieldAndValueWithIcon';
import { FedexExpeditionPickupOutDTO, GlobalTimeslots, OpeningEntityTypeEnum, OpeningModeEnum } from '@api/ringil-api.v2';
import { ApiContext } from '@api/api';
import useForm from '@hooks/useForm/useForm';
import * as yup from 'yup';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import ManageTimeslot, { timeslotValidationSchema } from '@components/obsolete/TimeslotWithDefaults/ManageTimeslot.tsx';
import FedexPickupForm, { FedexPickupValue, timeslotLocalDay } from '@components/RingilComponents/FedexPickupForm.tsx';
import { InputCheckbox } from '@components/ringil3/Input/InputCheckbox/InputCheckbox.tsx';
import dayjs from 'dayjs';

interface ComponentProps {
  applicationId: string;
  expeditionId: string;
  timeslots: GlobalTimeslots;
  localityName: string;
  companyLocationId: string;
  locationId: string;
  // FedEx pickup is booked at the first location - only there it can be rebooked
  isFirstLocation?: boolean;
  onContinue: () => void;
}

interface EditExpeditionLocationForm {
  timeslots: GlobalTimeslots;
}

export const useCreateEditExpeditionTimeSlotsSidebar = () => {
  const [visible, setVisible] = useState(false);

  const EditExpeditionTimeSlotsSidebar: React.FC<ComponentProps> = ({ onContinue, applicationId, locationId, expeditionId, timeslots, companyLocationId, isFirstLocation = false }) => {
    const { tr } = useTranslationLgs();
    const { editCompanyLocationTimeslotExpedition, getFedexExpeditionPickup } = useContext(ApiContext);
    const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
    const [loading, setLoading] = useState(false);

    // FedEx missed the pickup or the loading day moved - rebook it together with the timeslot
    const [fedexPickup, setFedexPickup] = useState<FedexExpeditionPickupOutDTO | null>(null);
    const [updateFedexPickup, setUpdateFedexPickup] = useState(true);
    const [pickupValue, setPickupValue] = useState<FedexPickupValue>({});
    const [pickupValid, setPickupValid] = useState(false);

    useEffect(() => {
      if (!isFirstLocation) return;
      getFedexExpeditionPickup(expeditionId, locationId, undefined, data => {
        setFedexPickup(data);
        setPickupValue({ readyTime: data.readyTime, closeTime: data.closeTime });
      });
    }, [isFirstLocation, expeditionId, locationId]);

    const rebookFedexPickup = fedexPickup?.applicable === true && updateFedexPickup;

    const save = (data: EditExpeditionLocationForm) => {
      if (rebookFedexPickup && !pickupValid) return;
      setLoading(true);
      editCompanyLocationTimeslotExpedition(
        expeditionId,
        locationId,
        {
          timeslots: data.timeslots,
          fedexPickup: rebookFedexPickup
            ? { pickupDate: pickupValue.pickupDate, readyTime: pickupValue.readyTime, closeTime: pickupValue.closeTime }
            : undefined,
        },
        () => {
          setLoading(false);
          onContinue();
          setVisible(false);
        },
        null,
        {
          onValidationFailed: errors => {
            setBeValidationErrors(errors);
            setLoading(false);
          },
          onError: (_error, showStandardError) => {
            showStandardError();
            setLoading(false);
          },
        },
      );
    };

    const schema = yup.object().shape({ timeslots: timeslotValidationSchema() });
    const form = useForm<EditExpeditionLocationForm>(schema, { timeslots }, d => save(d), false, false);

    const openDays = (form.values.timeslots?.dayWithInterval ?? [])
      .filter(day => day.openingMode !== OpeningModeEnum.CLOSED)
      .map(day => timeslotLocalDay(day))
      .filter(Boolean)
      .sort();

    if (!visible) return null;

    return (
      <>
        <RingilSidebar
          visible={visible}
          onBack={() => setVisible(false)}
          onContinue={() => form.validateAndSend()}
          heading={tr('EditExpeditionTimeSlotsSidebar.ocekavanaManipulace', 'Očekávaná manipulace')}
          loading={loading}
        >
          <div className={'flex flex-col gap-4'}>
            <BackendValidationComponent />
            <VerticalFieldAndValueWithIcon
              label={tr('EditExpeditionTimeSlotsSidebar.forExpedition', 'For expedition')}
              valueText={applicationId}
              iconPath={'/images/icons/ringil3/forExpedition.svg'}
            />
          </div>
          <>
            <ManageTimeslot
              entityId={companyLocationId}
              entityType={OpeningEntityTypeEnum.LOCATION}
              timeslot={form.values.timeslots}
              onChange={data => {
                form.useProduce(draft => {
                  draft.timeslots = data;
                });
              }}
              validationErrors={form.validationErrors}
              validationPrefix={'timeslots.'}
            />
            {fedexPickup?.applicable && (
              <div className="flex flex-col gap-3 mt-4 p-4 rounded-lg bg-base-tertiary">
                {fedexPickup.currentPickup && (
                  <VerticalFieldAndValueWithIcon
                    label={tr('EditExpeditionTimeSlotsSidebar.currentFedexPickup', 'Current pickup')}
                    valueText={`${fedexPickup.currentPickup.pickupConfirmationCode}, ${dayjs(fedexPickup.currentPickup.scheduledDate).format('DD.MM.YYYY')}`}
                    iconPath={'/images/icons/ringil3/forExpedition.svg'}
                  />
                )}
                <InputCheckbox
                  label={tr('EditExpeditionTimeSlotsSidebar.updateFedexPickup', 'Update FedEx pickup')}
                  value={updateFedexPickup}
                  onChange={(_, value) => setUpdateFedexPickup(!!value)}
                />
                {updateFedexPickup && (
                  <FedexPickupForm
                    openDays={openDays}
                    loadAvailability={(date, onData, onFailed) =>
                      getFedexExpeditionPickup(expeditionId, locationId, date, data => onData(data.availability), null, {
                        onError: (_error, showStandardError) => {
                          showStandardError();
                          onFailed();
                        },
                      })
                    }
                    value={pickupValue}
                    onChange={setPickupValue}
                    onValidChange={setPickupValid}
                  />
                )}
              </div>
            )}
          </>
        </RingilSidebar>
      </>
    );
  };

  return { EditExpeditionTimeSlotsSidebar, setVisible, visible };
};
